import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogFooter } from "./EditPercentagesDialog";

export function PoolDeleteConfirmModal({
    open,
    onClose,
    onConfirm,
    poolName = "",
    hasHistory = false,
    busy = false,
}) {
    const [confirmName, setConfirmName] = useState("");
    const [acknowledged, setAcknowledged] = useState(false);

    const handleClose = () => {
        setConfirmName("");
        setAcknowledged(false);
        onClose();
    };

    const nameMatches = confirmName.trim() === poolName;
    const canConfirm = nameMatches && acknowledged;

    const handleConfirm = async () => {
        if (!canConfirm || busy) return;
        await onConfirm();
        setConfirmName("");
        setAcknowledged(false);
    };

    if (!open) return null;

    return (
        <Dialog
            onClose={handleClose}
            title={`Delete "${poolName}"?`}
            subtitle={hasHistory
                ? "This pool will be archived — payout and audit history stay intact."
                : "This pool has no history and will be permanently removed."}
            testid="pool-delete-confirm-dialog"
            size="md"
        >
            <div className="space-y-4">
                <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-200/90 space-y-1.5">
                    <p className="font-medium">Before you continue</p>
                    <ul className="list-disc pl-4 space-y-1 text-amber-200/80">
                        <li>Pending approvals must be resolved first — deletion is blocked while any are open.</li>
                        {hasHistory ? (
                            <li>Payout and revenue history will be preserved; the pool disappears from your active list.</li>
                        ) : (
                            <li>This action removes the pool and its empty operational data.</li>
                        )}
                    </ul>
                </div>

                <div>
                    <p className="text-sm text-white/55 mb-2">
                        Type <span className="font-semibold text-white/80">{poolName}</span> and check the box below to confirm.
                    </p>
                    <input
                        type="text"
                        value={confirmName}
                        onChange={(e) => setConfirmName(e.target.value)}
                        placeholder={poolName}
                        className="input-dark w-full rounded-md px-3 py-2 text-sm"
                        autoComplete="off"
                        data-testid="pool-delete-confirm-input"
                    />
                </div>

                <label className="flex items-start gap-2.5 text-xs text-white/55 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={acknowledged}
                        onChange={(e) => setAcknowledged(e.target.checked)}
                        className="mt-0.5 rounded border-white/20"
                        data-testid="pool-delete-confirm-checkbox"
                    />
                    <span>I understand this will {hasHistory ? "archive" : "delete"} this pool and remove it from my active pool list.</span>
                </label>
            </div>
            <DialogFooter>
                <button onClick={handleClose} className="btn-ghost rounded-md px-4 py-2 text-sm" disabled={busy}>
                    Cancel
                </button>
                <button
                    onClick={handleConfirm}
                    disabled={!canConfirm || busy}
                    className="rounded-md px-4 py-2 text-sm font-medium inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-500 disabled:opacity-40 disabled:cursor-not-allowed text-white transition-colors"
                    data-testid="pool-delete-confirm-submit"
                >
                    {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                    {busy ? "Processing…" : hasHistory ? "Archive pool" : "Delete pool"}
                </button>
            </DialogFooter>
        </Dialog>
    );
}
